import { useTranslation } from 'react-i18next';
import {
  getAddOnByVariantId,
  trackAddToCart,
  trackViewCart,
} from 'components/pages/product/helpers';
import { useUpdateLineItemQuantity } from 'typescript/hooks/cart_client';
import { useToaster } from 'typescript/hooks/use_toaster';
import { localizedShopPath } from 'utilities/locales';
import { updateCartCount } from 'utilities/nav';

interface UseUpdateItemQuantityProps {
  currentProduct: Record<string, any>;
  cart: Record<string, any>;
  setCart: (cart: Record<string, any>) => void;
  selection: {
    quantity: number;
    addOns: Record<string, string>;
  };
  addOns: Record<string, any>;
  onMainProductAdded: () => void;
}

interface OnUpdateItemQuantityProps {
  action?: 'add' | 'update' | 'remove';
  quantity: number;
  addOnIds?: string[];
  bopisLocationId?: string;
  bopisSearchPostalCode?: string;
}

export const useUpdateItemQuantity = ({
  currentProduct,
  cart,
  setCart,
  selection,
  addOns,
  onMainProductAdded,
}: UseUpdateItemQuantityProps) => {
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const { updateLineItemQuantity, isPending } = useUpdateLineItemQuantity();

  const onUpdateItemQuantity = ({
    action = 'add',
    quantity,
    addOnIds = [],
    bopisLocationId = null,
    bopisSearchPostalCode = null,
  }: OnUpdateItemQuantityProps) => {
    updateLineItemQuantity(
      {
        variantToken: currentProduct.variant_token,
        quantity,
        addOnIds,
        action,
        bopisLocationId,
        bopisSearchPostalCode,
      },
      {
        onSuccess: (response) => {
          trackAddToCart({
            mainProductTrackingData: currentProduct.tracking_data,
            addOnsTrackingData: addOnIds?.map(
              (addOnId) => getAddOnByVariantId(addOns, addOnId).tracking_data,
            ),
            cartNumber: cart.number,
            quantity: selection.quantity,
          });

          // For Square 1pp hardware products, scroll to the upsell accordion and mark as added
          if (currentProduct.featured_product) {
            onMainProductAdded();
          }

          // update the cart state
          setCart(response.cart);

          // update the cart item count for public web nav
          updateCartCount(response.cart.item_count);

          showToast({
            message: t('productPage.addedToCart', {
              name: currentProduct.name,
            }),
            variant: 'success',
            actionLinks: [
              {
                href: `${localizedShopPath()}/cart`,
                text: t('productPage.viewCart'),
                onClick: () => {
                  trackViewCart({
                    orderNumber: cart.number,
                    trackingData: currentProduct.tracking_data,
                  });
                },
              },
            ],
          });
        },
        onError: () => {
          showToast({
            message: t('productPage.addToCartError', {
              name: currentProduct.name,
            }),
            variant: 'critical',
          });
        },
      },
    );
  };

  return {
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading: isPending,
  };
};
